<script lang="ts" setup>
import {
  DefaultButton,
  LockIcon,
  LogoIcon,
  NavMain,
  NavMainItem
} from '@hantecmarket/hantec-ui-widget'
import { onMounted, ref, computed } from 'vue'

import ChatBot from '@/components/ChatBot.vue'
import HantecLogo from '@/components/Icons/HantecLogo.vue'
import { getUrlLang } from '@/helpers/language-helper'
import { getLoadedStateLower, useNavigationLowerData } from '@/services/lowerNavigationService'
import { getLoadedState, useNavigationData } from '@/services/navigationService'
import { getEnv } from '@/utils/environmentService'
import { __, resolveRouteName, resolveButterUrl } from '@/utils/utils'

import MegaMenu from './MegaMenu.vue'

import type { NavItem } from '@/types/types'

const isTablet = ref<boolean>(false)

const checkDeviceType = (): void => {
  const userAgent: string = navigator.userAgent.toLowerCase()

  const isTouchDevice: boolean = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  const isTabletDevice: boolean = /ipad|android|tablet/.test(userAgent)

  isTablet.value = isTouchDevice && isTabletDevice
}

const navData = ref<NavItem[]>([])
const navDataLower = ref<any>([])

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null
  return null
}

const isExternalUrl = (url: string | undefined): boolean => {
  return !!(url?.startsWith('http://') || url?.startsWith('https://'))
}

const country = computed(() => {
  return getCookie('country') || ''
})

const countryMenus = computed(() => {
  let menus = navData?.value || []
  if (country.value === 'KR' || country.value === 'BR') {
    menus = menus.filter((menu) => menu.url !== 'partners')
  }
  if (country.value !== 'JP') {
    menus = menus.filter((menu) => !isExternalUrl(menu?.url))
  }
  return menus
})

const isLoaded = computed(() => getLoadedState() && getLoadedStateLower())
const language = ref<string>('')
language.value = getUrlLang()

onMounted(async () => {
  checkDeviceType()

  if (isTablet) {
    document.addEventListener('click', handleClickOutside)
  }
  navData.value = await useNavigationData()
  navDataLower.value = await useNavigationLowerData()
  window.addEventListener('close-menu', () => {
    changeActiveMenu()
  })
  lang =
    localStorage.getItem('lang') !== '' &&
    localStorage.getItem('lang') !== 'undefined' &&
    localStorage.getItem('lang') !== 'en'
      ? localStorage.getItem('lang') + '/'
      : ''
})

const activeMenu = ref<string | null>(null)

// Check if target element is child of another component
const isDescendantOf = (element: any, id: any) => {
  // Traverse up the DOM tree
  while (element) {
    if (element.id === id) {
      return true
    }
    element = element.parentElement
  }
  return false
}

const handleHoverMenu = (payload: any) => {
  if (!isTablet.value) {
    activeMenu.value = payload
    const event = new CustomEvent('change-active-menu', {
      detail: payload
    })
    window.dispatchEvent(event)
  }
}

const handleClickMenu = (payload: any) => {
  if (activeMenu.value === payload.target.innerHTML) {
    activeMenu.value = null
    const event = new CustomEvent('mouse-out-menu')
    window.dispatchEvent(event)
  } else {
    activeMenu.value = payload.target.innerHTML
    const event = new CustomEvent('change-active-menu', {
      detail: payload.target.innerHTML
    })
    window.dispatchEvent(event)
  }
}

const handleClickOutside = (event: any): void => {
  const isMenuItem = countryMenus.value.find((e) => e.label === event.target.id)
  const isInsideMenu = isDescendantOf(event.target, 'mega-menu')
  if (isMenuItem || isInsideMenu || event.target.id === 'mega-menu') {
    return
  } else {
    activeMenu.value = null
    const event = new CustomEvent('mouse-out-menu')
    window.dispatchEvent(event)
  }
}

const handleMouseOut = (event: any) => {
  if (!isTablet.value) {
    const isMenuItem = countryMenus.value.find((e) => e.label === event.target.id)
    const isInsideMenu = isDescendantOf(event.target, 'mega-menu')
    if (isMenuItem || isInsideMenu || event.target.id === 'mega-menu') {
      if (isMenuItem && !isExternalUrl(isMenuItem.url)) {
        handleHoverMenu(event.target.innerHTML)
      }
    } else {
      activeMenu.value = null
      const event = new CustomEvent('mouse-out-menu')
      window.dispatchEvent(event)
    }
  }
}

const changeActiveMenu = () => {
  activeMenu.value = null
}
const siteUrl = `${document.location.origin}/`
let lang: string = ''

const buttonLoginTitle = computed(() => navDataLower?.value[0]?.logintitle)
const buttonLoginUrl = computed(() => navDataLower?.value[0]?.loginurl)
const buttonRegisterTitle = computed(() => navDataLower?.value[0]?.openaccounttitle)
const buttonRegisterUrl = computed(() => navDataLower?.value[0]?.openaccounturl)
const buttonLoginUrlMu = computed(() => navDataLower?.value[0]?.loginurlmu)
const loginUrl =
  getEnv() !== 'hk' ? 'https://portal.hmarkets.' + getEnv() + '/' : 'https://portal.hmglobal.hk/'
</script>

<template>
  <NavMain
    :logoUrl="resolveRouteName('home')"
    class="sticky bg-hantec-secondary"
    mobile-button-breakpoint-class="2lg:hidden"
    nav-breakpoint-class="2lg:flex"
    @mouseover="handleMouseOut($event)"
  >
    <template v-slot:logo>
      <LogoIcon class="text-black md:hidden" />
      <HantecLogo class="hidden md:block pl-[2px] pr-3"></HantecLogo>
    </template>

    <ul
      v-if="isLoaded"
      class="flex items-center gap-2.5 divide-x divide-white text-[14px] tracking-normal 2lg:hidden"
    >
      <ChatBot />
    </ul>

    <template v-slot:menu>
      <MegaMenu v-if="isLoaded" :menus-data="countryMenus" class="block 2lg:hidden"></MegaMenu>
      <NavMainItem v-if="!isLoaded">
        <div class="animate-pulse w-screen-1/2" role="status">
          <div
            class="h-2.5 bg-gray-300 rounded-full dark:bg-gray-700 max-w-[640px] mb-2.5 mx-auto"
          ></div>

          <span class="sr-only">Loading...</span>
        </div>
      </NavMainItem>
      <template v-if="isLoaded">
        <NavMainItem
          v-for="item of countryMenus"
          :id="item.label"
          :key="item.url"
          :activeClass="item.label === activeMenu ? 'text-black bg-white' : ''"
          :isActive="item.label === activeMenu"
          class="hidden text-center def:text-white 2lg:block 2lg:flex 2lg:h-full 2lg:items-center"
          @click.stop="(e: any) => (isExternalUrl(item.url) ? null : handleClickMenu(e))"
        >
          <a
            v-if="isExternalUrl(item.url)"
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
            class="w-full h-full flex items-center justify-center text-white pointer-events-auto"
          >
            {{ item.label }}
          </a>
          <template v-else>
            {{ item.label }}
          </template>
        </NavMainItem>
      </template>
    </template>
    <div class="flex gap-2.5 md:py-5" v-if="isLoaded">
      <DefaultButton
        :href="resolveButterUrl(buttonRegisterUrl)"
        class="text-white px-1.5 md:px-2.5 md:py-4 hidden 2lg:flex items-center uppercase text-xs md:text-base md:leading-normal font-bold h-12.5 text-center"
      >
        <div>{{ buttonRegisterTitle || __('Open an Account') }}</div>
      </DefaultButton>

      <DefaultButton
        :href="`${loginUrl}${language === '' ? 'en/' : language === 'zh-hans' ? 'cns/' : language === 'zh-hant' ? 'cnt/' : language + '/'}login`"
        class="hidden 2lg:flex bg-transparent sm:bg-white text-gray-50 sm:text-black justify-between items-center px-2.5 py-4 sm:text-base sm:leading-normal font-bold h-12.5"
      >
        <div class="w-5 mr-2.5">
          <LockIcon />
        </div>
        <div class="normal-case md:uppercase">{{ buttonLoginTitle || __('Login') }}</div>
      </DefaultButton>
    </div>
    <template v-slot:mega-menu>
      <MegaMenu v-if="isLoaded" :menus-data="countryMenus" class="hidden 2lg:block"></MegaMenu>
    </template>
  </NavMain>
</template>
